/** @prettier */

import {useEffect, useState} from 'react';
import {Grid, Card, CardContent, Stack, Box} from '@mui/material';
import {useLocation, useParams, Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
import GroupAddDialog from './GroupAddDialog';
import GroupDeleteDialog from './GroupDeleteDialog';
import GroupEditDialog from './GroupEditDialog';
import CardContents from '../../component/CardContents';
//API import
import {getCompositionsGroups} from '../../API/API_Collection';
//Style import
import '../../css/common/common.css';

import {useOutlet} from './HCCVQGroupMain';
import {sortByUnicode} from '../../functions/sort';
import {iconBlack} from '../../define';

/**
 * グループ一覧
 * @returns JSX
 */
const GroupRegistration = (props: {unitSystem: 'RT' | 'Modbus'}) => {
  const trans = useTranslation().t; //翻訳関数
  //親コンポーネントから値を取得
  const {allGroupInfo, setAllGroupInfo} = useOutlet();
  //URL取得
  const location = useLocation();
  //パスパラメータ取得
  const {siteID} = useParams();

  const [registeredGroup, setRegisteredGroup] = useState(false); //登録グループの有無
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false); //編集ダイアログ表示フラグ
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false); //削除ダイアログ表示フラグ
  const [selectedGroupID, setSelectedGroupID] = useState(''); //選択したグループのID

  /** グループ追加ボタンの名前 */
  const addButtonName = trans('HCCVQGroupRegistration.buttonTxt.addGroup');

  //グループの情報取得
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    getCompositionsGroups(siteID, {'max-result': 400}).then(response => {
      //表示内容をUnicode順に並び替え
      response['group-informations'].sort(sortByUnicode('group-name'));
      for (const info of response['group-informations']) {
        if (info['unit-informations'] === undefined) continue;
        info['unit-informations'].sort(sortByUnicode('unit-name'));
      }

      setAllGroupInfo(response);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteID]);

  //表示するグループの空調機が登録されているグループを探索
  useEffect(() => {
    if (allGroupInfo === null) {
      return;
    }

    setRegisteredGroup(false);
    const isGroup = allGroupInfo['group-informations'].some(groupInfo => {
      return groupInfo['unit-system'] === props.unitSystem;
    });
    setRegisteredGroup(isGroup);
  }, [allGroupInfo, props.unitSystem]);

  /**
   * Editボタン押下時処理
   * @param groupID グループID
   */
  const openEditDialog = (groupID: string) => {
    setIsOpenEditDialog(true);
    setSelectedGroupID(groupID);
  };
  /**
   * Deleteボタン押下時処理
   * @param groupID グループID
   */
  const openDeleteDialog = (groupID: string) => {
    setIsOpenDeleteDialog(true);
    setSelectedGroupID(groupID);
  };

  /**
   * 歯車メニューリスト
   * @param groupID グループID
   * @returns メニュー一覧
   */
  const gearMenuItem = (groupID: string): GearMenuListArgType['menuItem'] => {
    return [
      {
        id: `mv-groupEdit${groupID}`,
        label: trans('HCCVQGroupRegistration.menuItem.edit'),
        onClick: () => openEditDialog(groupID),
        icon: {
          className: 'groupSetting__contents__button__icon',
          src: iconBlack.EDIT.src,
          alt: trans(
            `HCCVQGroupRegistration.image.alternativeText.${iconBlack.EDIT.alt}`
          ),
        },
      },
      {
        id: `mv-groupDelete${groupID}`,
        label: trans('HCCVQGroupRegistration.menuItem.delete'),
        onClick: () => openDeleteDialog(groupID),
        icon: {
          className: 'groupSetting__contents__button__icon',
          src: iconBlack.DELETE.src,
          alt: trans(
            `HCCVQGroupRegistration.image.alternativeText.${iconBlack.DELETE.alt}`
          ),
        },
      },
    ];
  };

  return (
    <>
      <Box className="groupSetting__title">
        {props.unitSystem === 'RT'
          ? trans('HCCVQGroupRegistration.title.hyozan')
          : trans('HCCVQGroupRegistration.title.hccvq')}
      </Box>
      <Stack spacing={4}>
        {/* グループ追加ボタン */}
        <Box className="groupSetting__contents--group groupSetting__contents__button--add">
          <GroupAddDialog
            addButtonName={addButtonName}
            unitSystem={props.unitSystem}
            setGroupInfo={setAllGroupInfo}
            groupInfoData={allGroupInfo}
          />
        </Box>
        {/** 登録済グループが一つも無い場合は、グループ追加ボタンのみ表示 */}
        {allGroupInfo === null || registeredGroup === false ? (
          <Card className="groupSetting__contents--group">
            <CardContent
              className="groupSetting__contents__message--noGroup"
              id="mv-noRegist-group">
              {trans('HCCVQGroupRegistration.instruction', {
                name: addButtonName,
              })}
            </CardContent>
          </Card>
        ) : (
          //録済グループ
          allGroupInfo['group-informations']
            //表示するグループに登録されている空調機と同じ通信設定の空調機が登録されているグループのみを表示
            .filter(groupInfo => {
              return groupInfo['unit-system'] === props.unitSystem;
            })
            .map(groupInfo => (
              <Card
                id={`groupBox-${groupInfo['group-id']}`}
                key={groupInfo['group-id']}
                className="groupSetting__contents--group">
                <CardContent>
                  <Stack spacing={2}>
                    {/* グループ名称と歯車 */}
                    <Grid
                      container
                      justifyContent="space-between"
                      className="groupSetting__contents__title">
                      {/* グループ名称 */}
                      <Grid item xs="auto">
                        <Link
                          to={`${location.pathname}/${groupInfo['group-id']}/${
                            props.unitSystem === 'RT'
                              ? 'hyozan-group-layout-registration'
                              : 'modbus-group-layout-registration'
                          }${location.search}`}
                          id={`mv-linkGpSetting${groupInfo['group-name']}`}>
                          {groupInfo['group-name']}
                        </Link>
                      </Grid>
                      {/* 歯車ボタン */}
                      <Grid item xs={1}>
                        <GearMenu
                          id={`mv-btnModbusGroupSettingOpen${groupInfo['group-id']}`}
                          menuItem={gearMenuItem(groupInfo['group-id'])}
                        />
                        {groupInfo['group-id'] === selectedGroupID &&
                        groupInfo['group-name'] !== undefined ? (
                          <>
                            <GroupEditDialog
                              groupID={groupInfo['group-id']}
                              displayGroupInfo={groupInfo}
                              setGroupInfo={setAllGroupInfo}
                              isOpenDialog={isOpenEditDialog}
                              setIsOpenDialog={setIsOpenEditDialog}
                            />
                            <GroupDeleteDialog
                              groupID={groupInfo['group-id']}
                              groupName={groupInfo['group-name']}
                              setGroupInfo={setAllGroupInfo}
                              isOpenDialog={isOpenDeleteDialog}
                              setIsOpenDialog={setIsOpenDeleteDialog}
                            />
                          </>
                        ) : (
                          <></>
                        )}
                      </Grid>
                    </Grid>
                    {/* グループに登録済の室内機合計数と室内機名称一覧 */}
                    <Stack
                      spacing={2}
                      className="groupSetting__contents__contents">
                      {/* グループに登録済の室内機合計数 */}
                      <CardContents
                        class="groupSetting__contents__contents__underLine"
                        valueDescription={trans(
                          'HCCVQGroupRegistration.item.title.numberOfUnits'
                        )}
                        value={
                          groupInfo['registrations-number'] === undefined
                            ? ''
                            : groupInfo['registrations-number']
                        }
                      />
                      {/* グループに登録済の室内機名称一覧 */}
                      <CardContents
                        class="groupSetting__contents__contents__underLine"
                        valueDescription={trans(
                          'HCCVQGroupRegistration.item.title.indoorUnit'
                        )}
                        value={
                          groupInfo['unit-informations'] === undefined ? (
                            <></>
                          ) : (
                            <Stack spacing={0}>
                              {groupInfo['unit-informations'].map(unitInfo => (
                                <Box key={unitInfo['unit-id']}>
                                  {unitInfo['unit-name']}
                                </Box>
                              ))}
                            </Stack>
                          )
                        }
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
        )}
      </Stack>
    </>
  );
};
export default GroupRegistration;
